import {Button} from '@dabafinance/react-components';
import {
  DatePicker,
  Dropdown,
  Form,
  Input,
  Pagination,
  Select,
  Switch,
  Table,
  Tag,
  Space,
  Typography,
  Divider,
} from 'antd';
import {useState} from 'react';
import styles from './notifications.module.scss';
import {useNavigate} from 'react-router-dom';
import {useLazyQuery, useMutation, useQuery} from '@apollo/client';
import {
  DELETE_NOTIFICATION,
  GET_SCHEDULED_NOTIFICATIONS,
  GET_SENT_NOTIFICATIONS,
  UPDATE_NOTIFICATION_STATUS,
} from 'graphql/mutations/notifications';
import moment from 'moment';
import {useSelector} from 'react-redux';
import {NavTabsEnum} from 'utils/constants';
import {makeSelectTabOperations} from 'redux/store/auth';
import {getUserTimezone} from 'utils/helper';
import dotsIcon from 'images/dots.svg';
import {ConfirmModal} from 'pages/adminOpportunities/stocksListings/ViewOrders/HandleOrders';
import SubmitButton from 'components/Startup/components/SubmitButton';
import {toast} from 'react-toastify';
import DBModal from 'components/modal/modal';
import {InstrumentTargetedTag} from './components/HoldingsSnapshots';

/*
 * Builds the `getAllNotifications` query input for the scheduled-style tabs
 * (Scheduled / Inactive / Workflow), which share a single query + refetch.
 *
 * The communication handover names `getScheduledNotifications` as the list
 * API, and the Sent tab does use it. This one deliberately does not: the
 * handover's own §6.2 records that `getScheduledNotifications` checks its
 * `active` filter with a truthy test, so `active: false` is ignored and an
 * "Inactive" request silently returns active records too. The Inactive tab
 * here depends on that distinction, and `getAllNotifications` honours it via
 * `isActive: 'INACTIVE'`.
 *
 * Switch this over once the backend tests for boolean presence.
 */
const buildScheduledData = (tab, page, limit, workflowStatus) => {
  const base = {isScheduled: true, page, limit};
  if (tab === 'INACTIVE') {
    return {...base, isActive: 'INACTIVE', status: 'PENDING', workflow: false};
  }
  if (tab === 'WORKFLOW') {
    return {
      ...base,
      isActive: 'ACTIVE',
      status: workflowStatus,
      workflow: true,
    };
  }
  return {...base, isActive: 'ACTIVE', status: 'PENDING', workflow: false};
};

const Notifications = () => {
  const [activeTab, setActiveTab] = useState('PENDING');
  // const [activeStatus, setActiveStatus] = useState('ACTIVE');
  // const [scheduledStatus, setScheduledStatus] = useState('PENDING');
  const [sentPage, setSentPage] = useState(1);
  const [sentLimit, setSentLimit] = useState(10);
  const [sentSearch, setSentSearch] = useState('');
  const [sentStartDate, setSentStartDate] = useState(null);
  const [sentEndDate, setSentEndDate] = useState(null);
  const [workflowStatus, setWorkflowStatus] = useState('PENDING');
  const [schedulePage, setSchedulePage] = useState(1);
  const [scheduleLimit, setScheduleLimit] = useState(10);
  const [openUpdateStatusModal, setOpenUpdateStatusModal] = useState({
    open: false,
    id: null,
    draft: null,
    isActive: null,
  });

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const {
    loading: loadingScheduled,
    data,
    refetch,
  } = useQuery(GET_SCHEDULED_NOTIFICATIONS, {
    variables: {
      data: {
        isActive: 'ACTIVE',
        isScheduled: true,
        status: 'PENDING',
        workflow: false,
        page: 1,
        limit: 10,
      },
    },
    skip: activeTab === 'SENT',
    fetchPolicy: 'no-cache',
    onError: error => toast.error(error.message),
    notifyOnNetworkStatusChange: true,
  });

  const [deleteNotification, {loading: loadingDelete}] = useMutation(
    DELETE_NOTIFICATION,
    {
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  const [
    getSentNotifications,
    {data: sentData, loading: loadingSent, refetch: refetchSent},
  ] = useLazyQuery(GET_SENT_NOTIFICATIONS, {
    variables: {
      input: {status: 'SENT', page: 1, limit: 10},
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
  });

  const tabOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.NotificationSchedule),
  );

  const scheduledColumns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (title, record) => (
        <>
          {title}
          {record?.instrumentTargeted && <InstrumentTargetedTag />}
        </>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (action, record) => (
        <div>
          {record?.category.map(name => (
            <Tag
              color={
                {
                  'PUSH': 'blue',
                  'EMAIL': 'red',
                  'SMS': 'green',
                  'WHATSAPP': 'yellow',
                  'IN_APP': 'purple',
                }[name]
              }
              style={{marginRight: 5, marginBottom: 5}}
              key={name}>
              {name?.replaceAll('_', ' ')}
            </Tag>
          ))}
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'notificationStatus',
      render: (action, record) => (
        <Tag
          color={
            {
              'PENDING': 'yellow',
              'SENT': 'green',
              'FAILED': 'red',
              'DRAFT': 'gray',
            }[record.status]
          }>
          {record.status}
        </Tag>
      ),
    },
    {
      title: 'Scheduled Date',
      dataIndex: 'time',
      key: 'createdAt',
      render: (action, record) => (
        <div>
          <span>
            {moment(record.schedule.date).format('LLL')} ({getUserTimezone()})
          </span>
        </div>
      ),
    },
    {
      title: 'Active',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (action, record) => (
        <Tag color={record.isActive ? 'green' : 'red'}>
          {record.isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      dataIndex: 'operation',
      key: 'actions',
      render: (text, record) => (
        <Dropdown
          menu={{
            items: [
              {
                label: 'Edit Notification',
                key: 'edit',
                onClick: () =>
                  navigate(`/dashboard/notification/update/${record?.id}`),
              },
              {
                label: 'Update Status',
                key: 'updateStatus',
                disabled: !tabOperations?.update || activeTab === 'SENT',
                onClick: () =>
                  setOpenUpdateStatusModal({
                    open: true,
                    id: record?.id,
                    draft: record?.draft,
                    isActive: record?.isActive,
                  }),
              },
              {
                label: 'Delete',
                key: 'delete',
                onClick: () => setOpenDeleteModal({open: true, id: record?.id}),
              },
            ],
          }}>
          <img src={dotsIcon} alt="dots" />
        </Dropdown>
      ),
    },
  ];

  const handleSwitchTab = tab => {
    setActiveTab(tab);
    if (tab === 'WORKFLOW') setWorkflowStatus('PENDING');
    if (tab === 'SENT' || tab === 'DRAFT') {
      setSentPage(1);
      setSentLimit(10);
      setSentSearch('');
      setSentStartDate(null);
      setSentEndDate(null);
      getSentNotifications({
        variables: {input: {status: tab, page: 1, limit: 10}},
      });
    } else {
      setSchedulePage(1);
      refetch({data: buildScheduledData(tab, 1, scheduleLimit, 'PENDING')});
    }
  };

  const handleFetchSentPage = (page, limit) => {
    if (page === sentPage && limit === sentLimit) return;
    setSentPage(page);
    setSentLimit(limit);
    getSentNotifications({
      variables: {
        input: {
          status: activeTab,
          page,
          limit,
          ...(sentSearch && {search: sentSearch}),
          ...(sentStartDate && {
            startDate: moment(sentStartDate).startOf('day').toISOString(),
          }),
          ...(sentEndDate && {
            endDate: moment(sentEndDate).endOf('day').toISOString(),
          }),
        },
      },
    });
  };

  const applySentFilters = () => {
    setSentPage(1);
    getSentNotifications({
      variables: {
        input: {
          status: 'SENT',
          page: 1,
          limit: sentLimit,
          ...(sentSearch && {search: sentSearch}),
          ...(sentStartDate && {
            startDate: moment(sentStartDate).startOf('day').toISOString(),
          }),
          ...(sentEndDate && {
            endDate: moment(sentEndDate).endOf('day').toISOString(),
          }),
        },
      },
    });
  };

  const handleWorkflowStatusChange = value => {
    setWorkflowStatus(value);
    setSchedulePage(1);
    refetch({data: buildScheduledData('WORKFLOW', 1, scheduleLimit, value)});
  };

  const handleSchedulePageChange = (page, limit) => {
    setSchedulePage(page);
    setScheduleLimit(limit);
    refetch({data: buildScheduledData(activeTab, page, limit, workflowStatus)});
  };

  const scheduleRows = data?.getAllNotifications || [];
  // getAllNotifications returns a bare list (no total), so infer whether a next
  // page exists: a full page implies there may be more.
  const scheduleTotal =
    scheduleRows.length < scheduleLimit
      ? (schedulePage - 1) * scheduleLimit + scheduleRows.length
      : schedulePage * scheduleLimit + 1;

  return (
    <>
      <div className={styles.header}>
        <h1>Notifications</h1>
        <Button
          label="Add New Notification"
          className={styles['button-style']}
          disabled={!tabOperations?.create}
          onClick={() => navigate('/dashboard/notifications/new')}
        />
      </div>
      <div className={styles['details-toggler']}>
        <span
          className={activeTab === 'PENDING' ? styles['active-toggle'] : ''}
          onClick={() => handleSwitchTab('PENDING')}>
          {' '}
          Scheduled
        </span>
        <span
          className={activeTab === 'DRAFT' ? styles['active-toggle'] : ''}
          onClick={() => handleSwitchTab('DRAFT')}>
          {' '}
          Draft
        </span>
        <span
          className={activeTab === 'INACTIVE' ? styles['active-toggle'] : ''}
          onClick={() => handleSwitchTab('INACTIVE')}>
          {' '}
          Inactive (Pending)
        </span>
        <span
          className={activeTab === 'SENT' ? styles['active-toggle'] : ''}
          onClick={() => handleSwitchTab('SENT')}>
          {' '}
          Sent
        </span>
        <span
          className={activeTab === 'WORKFLOW' ? styles['active-toggle'] : ''}
          onClick={() => handleSwitchTab('WORKFLOW')}>
          {' '}
          Workflow
        </span>
      </div>
      <div>
        {activeTab === 'SENT' && (
          <div className={styles.sentFilters}>
            <Form.Item style={{margin: 0}} name="startDate" label="Start Date">
              <DatePicker
                allowClear
                value={sentStartDate}
                onChange={setSentStartDate}
                size="middle"
                style={{width: 240, height: 45, borderRadius: 8}}
              />
            </Form.Item>
            <Form.Item style={{margin: 0}} name="endDate" label="End Date">
              <DatePicker
                allowClear
                value={sentEndDate}
                onChange={setSentEndDate}
                style={{width: 240, height: 45, borderRadius: 8}}
              />
            </Form.Item>
            <Input
              allowClear
              placeholder="Search title"
              value={sentSearch}
              onChange={e => setSentSearch(e.target.value)}
              style={{width: 200}}
            />
            <Button label="Apply Filters" onClick={applySentFilters} />
          </div>
        )}
        {activeTab === 'WORKFLOW' && (
          <div className={styles.sentFilters}>
            <Form.Item style={{margin: 0}} label="Status">
              <Select
                value={workflowStatus}
                onChange={handleWorkflowStatusChange}
                style={{width: 200}}
                options={[
                  {value: 'PENDING', label: 'Pending'},
                  {value: 'SENT', label: 'Sent'},
                  {value: 'FAILED', label: 'Failed'},
                ]}
              />
            </Form.Item>
          </div>
        )}
      </div>
      <Table
        loading={
          ['SENT', 'DRAFT'].includes(activeTab) ? loadingSent : loadingScheduled
        }
        className="antd-table"
        columns={scheduledColumns}
        dataSource={
          ['SENT', 'DRAFT'].includes(activeTab)
            ? sentData?.getScheduledNotifications?.data
            : scheduleRows
        }
        pagination={false}
      />
      {['SENT', 'DRAFT'].includes(activeTab) ? (
        <Pagination
          current={sentPage}
          onChange={handleFetchSentPage}
          total={sentData?.getScheduledNotifications?.pagination?.total || 0}
          showQuickJumper
          className="ant-table-pagination"
          showSizeChanger
          pageSize={sentLimit}
        />
      ) : (
        <Pagination
          current={schedulePage}
          onChange={handleSchedulePageChange}
          total={scheduleTotal}
          className="ant-table-pagination"
          showSizeChanger
          pageSize={scheduleLimit}
        />
      )}
      <ConfirmModal
        loading={loadingDelete}
        handleOperation={() => {
          deleteNotification({
            variables: {filters: {notificationId: openDeleteModal.id}},
          }).then(({data: {deleteScheduledNotifications}}) => {
            if (deleteScheduledNotifications.__typename === 'Error') {
              toast.error(deleteScheduledNotifications.message);
            } else {
              toast.success(deleteScheduledNotifications.message);
              setOpenDeleteModal({open: false, id: null});
              activeTab === 'DRAFT' ? refetchSent() : refetch();
            }
          });
        }}
        openModal={openDeleteModal.open}
        setOpenModal={() => setOpenDeleteModal(false)}
        title="Delete Notification"
        confirmText="Are you sure you want to delete this notification?"
      />
      <DBModal
        isOpen={openUpdateStatusModal.open}
        handleClose={() => {
          form.resetFields();
          setOpenUpdateStatusModal({
            open: false,
            id: null,
            draft: null,
            isActive: null,
          });
        }}
        content={
          <UpdateStatusModal
            id={openUpdateStatusModal.id}
            draft={openUpdateStatusModal.draft}
            isActive={openUpdateStatusModal.isActive}
            refetch={activeTab === 'DRAFT' ? refetchSent : refetch}
            form={form}
            onClose={() => {
              form.resetFields();
              setOpenUpdateStatusModal({
                open: false,
                id: null,
                draft: null,
                isActive: null,
              });
            }}
          />
        }
      />
    </>
  );
};

export {Notifications};

const UpdateStatusModal = ({id, draft, isActive, form, onClose, refetch}) => {
  const [updateNotificationState, {loading: loadingUpdate}] = useMutation(
    UPDATE_NOTIFICATION_STATUS,
    {
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  const status = Form.useWatch('status', form);
  const draftStatus = Form.useWatch('draft', form);

  const handleFinish = values => {
    updateNotificationState({
      variables: {
        input: {
          notificationId: id,
          isActive: values.status,
          draft: values.draft,
        },
      },
    }).then(({data: {updateNotificationState}}) => {
      if (updateNotificationState.__typename === 'Error') {
        toast.error(updateNotificationState.message);
      } else {
        toast.success(updateNotificationState.message);
        refetch();
        onClose();
      }
    });
  };

  const {Title, Text} = Typography;

  return (
    <div className={styles.updateStatusModal}>
      <Space direction="vertical" size="large" style={{width: '100%'}}>
        <div>
          <Title level={4} className={styles.updateStatusHeader}>
            Update Notification Status
          </Title>
          <Text type="secondary" className={styles.updateStatusDescription}>
            Modify the active status and draft state of this notification
          </Text>
        </div>

        <Divider className={styles.updateStatusDivider} />

        <Form
          layout="vertical"
          form={form}
          onFinish={handleFinish}
          initialValues={{
            status: isActive,
            draft: draft,
          }}>
          <Space direction="vertical" size="middle" style={{width: '100%'}}>
            <Form.Item
              name="status"
              label={
                <Text strong className={styles.statusLabel}>
                  Active Status
                </Text>
              }
              tooltip="Toggle whether this notification is active or inactive">
              <div className={styles.statusSwitchContainer}>
                <Text className={styles.statusText}>
                  {status !== undefined
                    ? status
                      ? 'Active'
                      : 'Inactive'
                    : isActive
                    ? 'Active'
                    : 'Inactive'}
                </Text>
                <Switch
                  checkedChildren="ON"
                  unCheckedChildren="OFF"
                  checked={status !== undefined ? status : isActive}
                  onChange={checked => form.setFieldsValue({status: checked})}
                />
              </div>
            </Form.Item>

            <Form.Item
              name="draft"
              label={
                <Text strong className={styles.statusLabel}>
                  Draft Status
                </Text>
              }
              tooltip="Toggle whether this notification is saved as a draft">
              <div className={styles.statusSwitchContainer}>
                <Text className={styles.statusText}>
                  {draftStatus !== undefined
                    ? draftStatus
                      ? 'Draft'
                      : 'Published'
                    : draft
                    ? 'Draft'
                    : 'Published'}
                </Text>
                <Switch
                  checkedChildren="Draft"
                  unCheckedChildren="Published"
                  checked={draftStatus !== undefined ? draftStatus : draft}
                  onChange={checked => form.setFieldsValue({draft: checked})}
                />
              </div>
            </Form.Item>
          </Space>

          <Divider className={styles.updateStatusFormDivider} />

          <div className={styles.statusActions}>
            <SubmitButton label="Update Status" disabled={loadingUpdate} />
          </div>
        </Form>
      </Space>
    </div>
  );
};
